import api from './api'

export interface EmployeeDocument {
  id: number
  user_id: number
  title: string
  file_path: string
  status: string
  created_at: string
  user?: {
    id: number
    email: string
    profile_picture?: string
    details?: {
      first_name: string
      last_name: string
    }
  }
}

export const employeeDocumentService = {
  async fetchAll(): Promise<EmployeeDocument[]> {
    const res = await api.get<EmployeeDocument[]>('/employee-documents')
    return res.data?.data || []
  },

  async create(data: FormData): Promise<EmployeeDocument> {
    const res = await api.post<EmployeeDocument>('/employee-documents', data, {
      headers: {
        Accept: 'application/json'
      }
    })
    return res.data?.data || res.data
  },

  async update(id: number, formData: FormData): Promise<EmployeeDocument> {
    formData.append('_method', 'PUT');
    const res = await api.post<EmployeeDocument>(`/employee-documents/${id}`, formData, {
      headers: { Accept: 'application/json' },
    })
    return res.data?.data || res.data
  },

  async delete(id: number): Promise<void> {
    await api.delete(`/employee-documents/${id}`)
  },

  getDownloadUrl(id: number): string {
    const base = (process.env.VUE_APP_API_URL || '/api').replace(/\/$/, '')
    return `${base}/employee-documents/${id}/download`
  },

  async printDocument(id: number): Promise<void> {
    const token = localStorage.getItem('token')
    const downloadUrl = this.getDownloadUrl(id)
    const response = await fetch(downloadUrl, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (!response.ok) throw new Error('Failed to fetch document for printing')

    const contentType = response.headers.get('content-type') || ''
    const blob = await response.blob()
    const isPdf = contentType.includes('pdf') || blob.type.includes('pdf')
    const isImage = contentType.startsWith('image/') || blob.type.startsWith('image/')

    if (!isPdf && !isImage) {
      const url = URL.createObjectURL(blob)
      window.open(url, '_blank')
      URL.revokeObjectURL(url)
      throw new Error('This file type cannot be printed directly. The file has been opened in a new tab.')
    }

    const blobUrl = URL.createObjectURL(blob)
    const printWindow = window.open(blobUrl, '_blank')
    if (!printWindow) {
      URL.revokeObjectURL(blobUrl)
      throw new Error('Popup blocked. Please allow popups to print this document.')
    }

    const triggerPrint = () => {
      try { printWindow.focus(); printWindow.print() } catch { /* ignore */ }
    }

    printWindow.onload = () => setTimeout(triggerPrint, 1000)
    setTimeout(triggerPrint, 3000)
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000)
  }
}
